import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import ActivityPerms from 'components/AccessControl/permissions.json';
import AccessControl from 'components/AccessControl';
import ActiveChip from 'components/Common/ActiveChip';

const CustomerAccountList = ({ customer, onConfirmAccountUnlink, jointAccountsEnabled, permissions }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedCertData, setSelectedCertData] = useState(null);

  const getAccountName = (item) => {
    if (item === null || (!item.accountNumber && !item.accountName)) {
      return 'N/A';
    }

    let accountName = item.accountNumber;

    if (!accountName) {
      accountName = item.accountName;
    } else if (item.accountName !== null && item.accountName !== accountName) {
      accountName = `${ accountName } - ${ item.accountName }`;
    }

    return accountName;
  };

  const getLegalEntityDescription = (item) => {
    if (!item || (!item.identifier && !item.name)) {
      return 'N/A';
    }

    let description = item.identifier;
    if (description && item.name) {
      description = `${ description } - ${ item.name }`;
    } else if (!description) {
      description = item.name;
    }

    return description;
  };

  const onExpandCollapse = (id) => {
    if (selectedRow === id) {
      setSelectedRow(null);
      setSelectedCertData(null);
    } else {
      setSelectedRow(id);
      if (customer && customer.accounts) {
        const selectedAccount = customer.accounts.filter(a => a.account.id === id);
        if (selectedAccount && selectedAccount.length === 1) {
          setSelectedCertData(selectedAccount[0].formCertificationData ?? selectedAccount[0].importCertificationData);
        }
      }
    }
  };

  const foreignPartnersSuffix = (id) => {
    if (customer && customer.accounts) {
      const selectedAccount = customer.accounts.filter(a => a.account.id === id);
      if (selectedAccount && selectedAccount.length === 1) {
        const certData = selectedAccount[0].formCertificationData ?? selectedAccount[0].importCertificationData;
        if (certData && certData.hasForeignPartners === true) {
          const classificationData = selectedAccount[0].reportingClassification ?? selectedAccount[0].withholdingClassification;
          if (classificationData && classificationData.chapter3Status === 8) {
            return ' - With Foreign Partners';
          }
          if (classificationData && classificationData.chapter3Status === 10) {
            return ' - With Foreign Beneficiaries';
          }
        }
      }
    }
    return '';
  };

  return (
    <div>
      <table className="table table-striped table-hover table-bordered">
        <thead>
          <tr>
            <th>Account</th>
            <th>Capacity</th>
            <th>Chapter 3 &amp; 4</th>
            <th>CRS Status</th>
            <th>Certification Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          { (customer != null && customer.accounts != null && customer.accounts.length > 0)
            && customer.accounts.map(accountDtl => accountDtl != null && accountDtl.account != null && <React.Fragment key={ accountDtl.account.id }>
              <tr key={ accountDtl.account.id }>
                <td align="center">
                  { AccessControl.hasPermission(permissions, [ActivityPerms.accountEdit, ActivityPerms.accountPageView])
                    ? <Link className="text-bold" to={ `/account/${ accountDtl.account.id }` }>{ getAccountName(accountDtl.account) }</Link>
                    : getAccountName(accountDtl.account) }
                  <ActiveChip isActive={ accountDtl?.account.isActive } />
                </td>
                <td align="center">{ accountDtl.capacityString }</td>
                <td align="center">
                  <b>Chapter 3 Status: </b>{ accountDtl.reportingClassification !== null && accountDtl.reportingClassification.chapter3Status !== null ? accountDtl.reportingClassification.chapter3StatusDescription : 'N/A'}
                  {foreignPartnersSuffix(accountDtl.account.id)}<br />
                  <b>Chapter 4 Status: </b>{ accountDtl.reportingClassification !== null && accountDtl.reportingClassification.chapter4Status != null ? accountDtl.reportingClassification.chapter4StatusDescription : 'N/A' }<br />
                  <b>Updated By: </b>{ accountDtl.reportingClassification !== null ? accountDtl.reportingClassification.recordSourceDescription : 'N/A' }
                </td>
                <td align="center">
                  { accountDtl.crsClassification !== null ? accountDtl.crsClassification.classificationDescription : 'N/A' }<br />
                  <b>Updated By: </b>{ accountDtl.crsClassification !== null ? accountDtl.crsClassification.recordSourceDescription : 'N/A' }
                </td>
                <td align="center">{ accountDtl.account.certificationStatusDescription }</td>
                <td align="center">
                  <div className={ jointAccountsEnabled ? 'form-action-btn-group' : '' }>
                    { jointAccountsEnabled && <AccessControl allowedPermissions={ [ActivityPerms.customerEdit] }>
                      <button type="submit" title="Unlink Account" className="btn btn-sm btn-danger" onClick={ () => onConfirmAccountUnlink(accountDtl.account.id) }><span className="icon icon-link" /></button>
                    </AccessControl> }
                    <button
                      type="button"
                      className="btn btn-sm"
                      title={ `${ selectedRow === accountDtl.account.id ? 'Hide' : 'Display' } additional details` }
                      onClick={ () => onExpandCollapse(accountDtl.account.id) }
                    >
                      { selectedRow === accountDtl.account.id ? '▲' : '▼' }
                    </button>
                  </div>
                </td>
              </tr>
              { selectedRow === accountDtl.account.id && <tr key={ `${ accountDtl.account.id }-additional` } className="child-row">
                <td colSpan="7" align="center">
                  <div className="container">
                    <div className="child-data-item">
                      <label>Legal Entity</label>
                      <span>{ getLegalEntityDescription(accountDtl.account?.legalEntity) }</span>
                    </div>
                    <div className="child-data-item">
                      <label>US TIN</label>
                      <span>{ selectedCertData?.tin ? selectedCertData.tin : 'N/A' }</span>
                    </div>
                    <div className="child-data-item">
                      <label>FTIN</label>
                      <span>{ selectedCertData?.foreignTin ? selectedCertData.foreignTin : 'N/A' }</span>
                    </div>
                    <div className="child-data-item">
                      <label>GIIN</label>
                      <span>{ selectedCertData?.giin ? selectedCertData.giin : 'N/A' }</span>
                    </div>
                    <div className="child-data-item">
                      <label>Part II GIIN</label>
                      <span>{ selectedCertData?.part2Giin ? selectedCertData.part2Giin : 'N/A' }</span>
                    </div>
                    <div className="child-data-item">
                      <label>Treaty Country</label>
                      <span>{ selectedCertData?.treatyCountry ? selectedCertData.treatyCountry : 'N/A' }</span>
                    </div>
                    { selectedCertData?.lobCategoryCode && selectedCertData.lobCategoryCode !== ''
                      && <div className="child-data-item">
                        <label>LOB</label>
                        <span>{ selectedCertData.lobCategoryCode ? selectedCertData.lobCategoryCode : 'N/A' }</span>
                      </div> }
                  </div>
                </td>
              </tr> }
            </React.Fragment>) }
          { (!customer || !customer.accounts || customer.accounts.length === 0) && <tr><td colSpan="7" style={ { textAlign: 'center' } }><p><b>No Data to Show</b></p></td></tr> }
        </tbody>
      </table>
    </div>
  );
};

CustomerAccountList.propTypes = {
  customer: PropTypes.shape(),
  onConfirmAccountUnlink: PropTypes.func.isRequired,
  jointAccountsEnabled: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string)
};

CustomerAccountList.defaultProps = {
  customer: {
    accounts: []
  },
  jointAccountsEnabled: false,
  permissions: null
};

export default CustomerAccountList;
